// src/redux/api/propertiesThunks.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import {Detail, PropertyFull, PropertyLight} from "../../../types";
import {deleteData, fetchData, patchData, postData, updateData} from "../../../services/data-service";
import {setOperation} from "../operations/operationsSlice";
import {pollOperationStatus} from "../operations/operationsThunks";
import { PROPERTIES_ENDPOINT } from "../../../services/utils/api-consts";
// Аргументы для создания/обновления
interface CreatePropertyArg {
    documentId: string;
    propertyTypeId: string;
    text: string;
}

interface UpdatePropertyArg {
    id: string;               // Property GUID
    documentId: string;       // Документ, куда мы хотим его «привязать» (может не меняться)
    oldDocumentId?: string;     // чтобы понять, был ли перенос
    propertyTypeId?: string;
    text?: string;
}

// Ответ сервера на create/update property:
interface CreateOrUpdatePropertyResponse {
    operationId: string;
    property: PropertyLight;
}


// ====== Создание свойства =====
export const createProperty = createAsyncThunk<
    { operationId: string; propertyId: string },
    CreatePropertyArg,
    { rejectValue: string }
>(
    'properties/createProperty',
    async ({ documentId, propertyTypeId, text }, { dispatch, rejectWithValue }) => {
        try {
            const url = `${PROPERTIES_ENDPOINT}?documentId=${documentId}`;
            const body = { propertyTypeId, text };

            const response = await postData<CreateOrUpdatePropertyResponse, any>(url, body);
            // response = { operationId, property: PropertyLight }

            // Допустим, property.id — GUID
            const propId = response.property.id;

            dispatch(
                setOperation({
                    id: response.operationId,
                    type: 'CREATE_PROPERTY',
                    status: 'InProgress',
                    progress: 0,
                    errorMessage: null,
                    createdAt: new Date().toISOString(),
                    completedAt: null,

                    // Запоминаем, какое свойство создали
                    meta: {
                        propertyId: propId,
                    },
                })
            );
            dispatch(pollOperationStatus(response.operationId));

            return {
                operationId: response.operationId,
                propertyId: propId,
            };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// ====== Обновление свойства (PUT /api/properties/{id}) =====
export const updateProperty = createAsyncThunk<
    { operationId: string; propertyId: string },
    UpdatePropertyArg,
    { rejectValue: string }
>(
    'properties/updateProperty',
    async ({ id, documentId, propertyTypeId, text }, { dispatch, rejectWithValue }) => {
        try {
            console.log(documentId)
            const url = `${PROPERTIES_ENDPOINT}${id}`;
            const body = { propertyTypeId, text };
            const response = await updateData<CreateOrUpdatePropertyResponse, any>(url, body);
            dispatch(
                setOperation({
                    id: response.operationId,
                    type: 'UPDATE_PROPERTY',
                    status: 'InProgress',
                    progress: 0,
                    errorMessage: null,
                    createdAt: new Date().toISOString(),
                    completedAt: null,

                    meta: {
                        propertyId: response.property.id,
                    },
                })
            );
            dispatch(pollOperationStatus(response.operationId));

            return {
                operationId: response.operationId,
                propertyId: response.property.id,
            };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);


export const fetchPropertyDetails = createAsyncThunk<
    PropertyFull,
    string, // propertyId
    { rejectValue: string }
>(
    'properties/fetchPropertyDetails',
    async (propertyId, { rejectWithValue }) => {
        try {
            const url = `${PROPERTIES_ENDPOINT}${propertyId}`;
            return await fetchData<PropertyFull>(url);
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// ====== Удаление свойства =====
export const deleteProperty = createAsyncThunk<
    string,          // Возвращаем ID удалённого свойства? Или documentId?
    { propertyId: string; documentId: string },
    { rejectValue: string }
>(
    'properties/deleteProperty',
    async ({ propertyId }, { rejectWithValue }) => {
        try {
            await deleteData(`${PROPERTIES_ENDPOINT}${propertyId}`);
            return propertyId;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// ====== Обновление деталей свойства =====
export const patchPropertyDetails = createAsyncThunk<
    { propertyId: string; details: Detail[] },
    { propertyId: string; details: { code: string; value: any }[] },
    { rejectValue: string }
>(
    'properties/patchPropertyDetails',
    async ({ propertyId, details }, { rejectWithValue }) => {
        try {
            const url = `${PROPERTIES_ENDPOINT}${propertyId}/details`;
            const updatedDetails = await patchData<Detail[], any>(url, { details });
            return { propertyId, details: updatedDetails };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);
